/* Motion.
 *
 * The two scroll-driven effects from Tim's elegance pass (24 Sep), in one file
 * because they share a viewport and a reduced-motion switch:
 *
 *   PARALLAX  - `[data-parallax]` frames. The media inside sits on a
 *               `.parallax__layer` that is taller than the frame by
 *               --parallax-range top and bottom (input.css), and drifts through
 *               that slack as the frame crosses the viewport. The slack is the
 *               only crop, so the range is kept small.
 *   REVEAL    - each section in <main> fades up the first time it scrolls into
 *               view. Sections already on screen at load are left alone, so the
 *               first paint never flashes empty.
 *   ACCORDION - every accordion (`details.accordion`, the collection filter
 *               groups, the bag's samples box) slides open and shut over
 *               --motion-base instead of snapping (Tim, 5 Oct). Rows sharing a
 *               `name` close each other with the same slide.
 *
 * All are progressive: with this file absent, or with reduced motion on, the
 * media sits still in its frame, every section is simply visible and the
 * accordions open as the browser opens them.
 */
(function () {
  if (window.__ddjMotion) return;
  window.__ddjMotion = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  /* ─── Parallax ─── */

  var frames = [];
  var visible = new Set();
  var ticking = false;

  function range(frame) {
    var value = parseFloat(getComputedStyle(frame).getPropertyValue('--parallax-range'));
    return isNaN(value) ? 0.06 : value / 100;
  }

  function paint() {
    ticking = false;
    var vh = window.innerHeight;
    visible.forEach(function (entry) {
      var rect = entry.frame.getBoundingClientRect();
      /* 0 as the frame's top edge enters from below, 1 as its bottom edge
       * leaves at the top. The layer travels from -range to +range across
       * that, so it is centred when the frame is mid-screen. */
      var progress = (vh - rect.top) / (vh + rect.height);
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      var y = (progress - 0.5) * 2 * entry.range * rect.height;
      entry.layer.style.transform = 'translate3d(0,' + y.toFixed(2) + 'px,0)';
    });
  }

  function request() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(paint);
  }

  var parallaxObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var entry = e.target.__ddjParallax;
          if (!entry) return;
          if (e.isIntersecting) visible.add(entry);
          else visible.delete(entry);
        });
        request();
      }, { rootMargin: '10% 0px' })
    : null;

  function bindParallax(scope) {
    if (!parallaxObserver) return;
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-parallax]'), function (frame) {
      if (frame.__ddjParallax) return;
      var layer = frame.querySelector('.parallax__layer');
      if (!layer) return;
      var entry = { frame: frame, layer: layer, range: range(frame) };
      frame.__ddjParallax = entry;
      frames.push(entry);
      parallaxObserver.observe(frame);
    });
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', function () {
    frames.forEach(function (entry) { entry.range = range(entry.frame); });
    request();
  });

  /* ─── Section reveal ─── */

  /* Skipped: the sticky bar, which is `position: fixed` and would be pinned to
   * its section by the reveal's transform; and anything a section opts out
   * with `data-no-reveal`. */
  var SKIP = '.sticky-bar-section, [data-no-reveal]';

  var revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.setAttribute('data-revealed', '');
          revealObserver.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' })
    : null;

  function bindReveal() {
    /* The theme editor re-renders sections constantly; a fade on every edit
     * would be noise to the merchant. */
    if (!revealObserver || (window.Shopify && window.Shopify.designMode)) return;
    var main = document.getElementById('main-content');
    if (!main) return;
    var vh = window.innerHeight;
    Array.prototype.forEach.call(main.querySelectorAll(':scope > .shopify-section'), function (section) {
      if (section.hasAttribute('data-reveal')) return;
      if (section.matches(SKIP) || section.querySelector('[data-no-reveal]')) return;
      if (!section.offsetHeight) return;
      /* Already on screen: leave it exactly as painted. */
      if (section.getBoundingClientRect().top < vh) return;
      section.setAttribute('data-reveal', '');
      revealObserver.observe(section);
    });
  }

  /* ─── Accordions ─── */

  var ACCORDION = 'details.accordion, details.collection-group, details.cart-samples';
  var desktop = window.matchMedia('(min-width: 768px)');

  function timing() {
    var css = getComputedStyle(document.documentElement);
    var ms = parseFloat(css.getPropertyValue('--motion-base'));
    return {
      duration: isNaN(ms) ? 320 : ms,
      easing: css.getPropertyValue('--ease-elegant').trim() || 'cubic-bezier(0.22, 1, 0.36, 1)'
    };
  }

  /* A row animates from the height it has to the height it will have, with
   * `open` set to its end state for the measurement (no paint in between). */
  function slide(details, opening, done) {
    if (details._slide) details._slide.cancel();
    var from = details.offsetHeight;
    details.open = true;
    var openHeight = details.offsetHeight;
    details.open = false;
    var closedHeight = details.offsetHeight;
    details.open = true;
    var to = opening ? openHeight : closedHeight;
    details.style.overflow = 'hidden';
    var anim = details.animate({ height: [from + 'px', to + 'px'] }, timing());
    details._slide = anim;
    function finish(cancelled) {
      if (details._slide === anim) details._slide = null;
      details.style.overflow = '';
      if (!cancelled && !opening) details.open = false;
      if (done) done();
    }
    anim.onfinish = function () { finish(false); };
    anim.oncancel = function () { finish(true); };
  }

  document.addEventListener('click', function (event) {
    var summary = event.target.closest('summary');
    var details = summary && summary.parentElement;
    if (!details || details.tagName !== 'DETAILS' || !details.matches(ACCORDION)) return;
    /* Desktop forms that are not accordions there: the footer's static
     * columns and the FAQs tabs. */
    if (desktop.matches && (details.matches('.accordion--md-static') || details.closest('.faq-tabs[data-tabs-ready]'))) return;
    event.preventDefault();

    /* Mid-slide the row is still `open`, so the slide's own direction says
     * which way a second click turns it. */
    var opening = details._slide ? !!details._slideClosing : !details.open;
    details._slideClosing = !opening;

    if (opening && details.name) {
      var name = details.name;
      document.querySelectorAll('details[name="' + name.replace(/"/g, '\\"') + '"]').forEach(function (other) {
        if (other === details || !other.open) return;
        /* Out of the group while it slides shut, or opening this row would
         * close it at once. */
        other.removeAttribute('name');
        other._slideClosing = true;
        slide(other, false, function () { other.setAttribute('name', name); });
      });
    }
    slide(details, opening);
  });

  function init() {
    bindParallax();
    bindReveal();
    request();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: a re-rendered section brings fresh, unbound frames. */
  document.addEventListener('shopify:section:load', function (event) {
    bindParallax(event.target);
    request();
  });
})();
